import { AgentConfigGet, AgentConfigSet } from '@tryiris-ai/mailroom-helper/agentConfig';
import { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { generateId } from '@studio/models';
import { wrapApiCall, toHexFromId, toIdFromHex } from '#~/api/index.js';
import { ApiError } from '#~/api/types.js';
import { nats } from '#~/nats/index.js';
import { logger } from '#~/logger/index.js';
import type { Agent } from '@studio/models/agent';

const VERSION = 1;
type Baseline = { version: number; revision: Uint8Array };
// A later read cannot silently authorize replacing a snapshot built from an
// earlier revision. Successful writes advance this session's baseline only.
const baselines = new Map<string, Baseline>();
const same = (a: Uint8Array, b: Uint8Array): boolean =>
  a.length === b.length && a.every((value, i) => value === b[i]);
// The requester caches identical reads for 10s and serves the cached reply on
// error. A read whose revision becomes a write precondition must bypass both,
// or a conflict re-read gets back the revision that just 409'd.
const FRESH = { force: true, staleOnError: false } as const;
const read = (agentId: Uint8Array, token: string, checkNativeWrite = false, fresh = false) =>
  wrapApiCall(
    () =>
      AgentConfigGet.request(
        nats.cloudConnection(),
        {
          agentId,
          token,
          nativePresetsVersion: VERSION,
          checkNativeWrite
        },
        fresh ? FRESH : undefined
      ),
    `Unable to get stored data: ${toHexFromId(agentId)}`
  );

/** Read capability and revision together with the snapshot they describe. */
export const getData = async (agentId: Uint8Array, token: string): Promise<Uint8Array> => {
  const id = toHexFromId(agentId)!;
  let result: Awaited<ReturnType<typeof read>>;
  try {
    result = await read(agentId, token);
  } catch (error) {
    const cause = error instanceof ApiError ? error.cause : undefined;
    if (
      error instanceof ApiError &&
      error.code === 404 &&
      cause &&
      typeof cause === 'object' &&
      'message' in cause &&
      cause.message === 'agent config not found'
    ) {
      // New Studios have no saved config until their first save. This is empty
      // inventory, not a restore failure or permission to write native presets.
      baselines.delete(id);
      return new Uint8Array();
    }
    throw error;
  }
  // A broker may recover from decode failure, but that cannot authorize camera
  // SET. Validate at the baseline owner and invalidate even an earlier receipt.
  try {
    State.decode(result.config);
  } catch (error) {
    baselines.delete(id);
    throw error;
  }
  if (!baselines.has(id))
    baselines.set(id, {
      version: result.nativePresetsVersion ?? 0,
      revision: Uint8Array.from(result.revision ?? [])
    });
  return result.config;
};

/** Check before camera SET; unknown readiness does not gate absolute capture. */
export const nativePresetPersistenceReady = async (
  agentId: string,
  token: string
): Promise<boolean> => {
  const result = await read(toIdFromHex(agentId)!, token, true, true);
  try {
    State.decode(result.config);
  } catch (error) {
    baselines.delete(agentId);
    throw error;
  }
  if (result.nativePresetsVersion !== VERSION || result.revision.length !== 32) return false;
  const baseline = baselines.get(agentId);
  if (!baseline || baseline.version !== VERSION || !same(baseline.revision, result.revision)) {
    return false;
  }
  return true;
};

/**
 * Whether this session's writes for an agent are conditional version 1
 * writes. Only then does a saved deletion of a native-only record reach the
 * stored row: a legacy write leaves native-only records to the backend's
 * preservation of the stored row, so such a record survives the save.
 */
export const nativeWritesPersist = (agentId: Agent['id']): boolean => {
  const baseline = baselines.get(agentId);
  return baseline?.version === VERSION && baseline.revision.length === 32;
};

/**
 * A legacy (version 0) write cannot carry native records, but refusing the whole
 * snapshot would block renames, groups and unrelated config after an API
 * rollback. Persist everything else: hybrid records keep their absolute pose,
 * native-only records are left to the backend's preservation of the stored row.
 */
const projectLegacySnapshot = (data: Uint8Array, id: string): Uint8Array => {
  const state = State.decode(data);
  let dropped = 0;
  let stripped = 0;
  for (const device of Object.values(state.control?.studio?.device ?? {})) {
    const items = device?.control?.presets?.items;
    if (!items) continue;
    for (const [presetId, preset] of Object.entries(items)) {
      if (!preset?.nativeTarget) continue;
      if (preset.position) {
        preset.nativeTarget = undefined;
        stripped += 1;
      } else {
        delete items[presetId];
        dropped += 1;
      }
    }
  }
  if (!dropped && !stripped) return data;
  logger.warning(
    `Native preset persistence is unavailable for ${id}; saving without ${dropped} native-only preset(s) and ${stripped} native target(s). Re-save them once the config service is ready.`
  );
  return State.encode(state).finish();
};

const isStaleRevision = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { code?: unknown }).code === 409;

/**
 * What a full write persists. `encode` is called when the write starts and
 * again after a stale-revision re-read, so every byte sent under a revision
 * was encoded after that revision was read or acknowledged. `canRetry` reports
 * whether a re-encode can include another writer's change: the agent
 * acknowledges a mutation before that writer's save, so a Studio in sync with
 * the agent already holds it. Without `canRetry` (or when it returns false) a
 * conflict surfaces instead of being retried.
 */
export type SnapshotSource = {
  encode: () => Uint8Array;
  canRetry?: () => boolean;
};

/** Capable full writes are conditional; legacy writes retain backend preservation. */
const writeData = async (
  agentId: Uint8Array,
  source: SnapshotSource,
  token: string
): Promise<void> => {
  const id = toHexFromId(agentId)!;
  let baseline = baselines.get(id);
  const capable = baseline?.version === VERSION && baseline.revision.length === 32;
  const snapshot = (): Uint8Array => {
    const data = source.encode();
    return capable ? data : projectLegacySnapshot(data, id);
  };
  const operationId = capable ? generateId() : '';
  const send = (config: Uint8Array, revision: Uint8Array) =>
    wrapApiCall(
      () =>
        AgentConfigSet.request(nats.cloudConnection(), {
          agentId,
          config,
          token,
          nativePresetsVersion: capable ? VERSION : 0,
          expectedRevision: revision,
          operationId
        }),
      `Unable to set stored data: ${id}`
    );
  let result: Awaited<ReturnType<typeof send>>;
  try {
    result = await send(snapshot(), capable ? baseline!.revision : new Uint8Array());
  } catch (error) {
    if (!capable || !isStaleRevision(error)) throw error;
    // Another writer advanced the row since this session pinned its revision
    // (e.g. after an encoder hard reset). Resending the bytes encoded before
    // the conflict under the fresh revision would authorize overwriting that
    // writer's change, so the retry re-encodes the live control tree after the
    // re-read, and only while this Studio is in sync with the agent. Otherwise
    // the conflict surfaces and the stored row keeps the other writer's edit.
    if (!source.canRetry?.()) throw error;
    logger.warning(`Stored data revision for ${id} is stale; re-reading before retrying the save.`);
    const fresh = await read(agentId, token, false, true);
    try {
      State.decode(fresh.config);
    } catch (cause) {
      baselines.delete(id);
      throw cause;
    }
    if (fresh.nativePresetsVersion !== VERSION || fresh.revision.length !== 32) {
      baselines.delete(id);
      throw error;
    }
    baseline = { version: VERSION, revision: Uint8Array.from(fresh.revision) };
    baselines.set(id, baseline);
    result = await send(snapshot(), baseline.revision);
  }
  if (capable) {
    if (
      result.nativePresetsVersion !== VERSION ||
      result.operationId !== operationId ||
      result.revision.length !== 32
    ) {
      throw new Error(
        'Unable to confirm native preset persistence. The config service must be deployed consistently.'
      );
    }
    baselines.set(id, { version: VERSION, revision: Uint8Array.from(result.revision) });
  }
};

const writes = new Map<string, Promise<void>>();
/**
 * Serialize this session's full writes so its own previous acknowledgement
 * advances the precondition. A `SnapshotSource` is encoded when its write
 * starts; fixed bytes are copied now and are never retried on a conflict,
 * because they were encoded before it.
 */
export const setData = (
  agentId: Uint8Array,
  data: SnapshotSource | Uint8Array,
  token: string
): Promise<void> => {
  const id = toHexFromId(agentId)!;
  let source: SnapshotSource;
  // isView, not instanceof: encoded protobuf bytes can come from another realm.
  if (ArrayBuffer.isView(data)) {
    const fixed = Uint8Array.from(data);
    source = { encode: () => fixed };
  } else {
    source = data;
  }
  const previous = writes.get(id) ?? Promise.resolve();
  const current = previous.catch(() => undefined).then(() => writeData(agentId, source, token));
  writes.set(id, current);
  void current
    .finally(() => {
      if (writes.get(id) === current) writes.delete(id);
    })
    .catch(() => undefined);
  return current;
};
